/**
 * Responsive image helpers.
 *
 * Every photo in /public/assets/images ships as several widths alongside a
 * plain fallback (e.g. service-tile.jpg + service-tile-800.jpg,
 * service-tile-1400.jpg, service-tile-2400.jpg). Browsers pick the smallest
 * file that still renders sharp on the device, so a phone downloads ~35KB
 * where a 4K desktop gets the full-resolution crop.
 *
 * Keep these width arrays in sync with the widths generated on disk — a width
 * listed here with no matching file would 404.
 */
export const HERO_WIDTHS = [1280, 1920, 2560];
export const SERVICE_WIDTHS = [800, 1400, 2400];
export const BLOG_WIDTHS = [900, 1800];

/** Build a `srcSet` string from a base image path and the widths available. */
export function srcSetFor(src: string, widths: number[]): string {
  if (!/\.jpg$/i.test(src)) return "";
  return widths.map((w) => `${src.replace(/\.jpg$/i, `-${w}.jpg`)} ${w}w`).join(", ");
}

/**
 * Same as `srcSetFor`, but pointing at the WebP derivatives that
 * `npm run images` writes next to every JPEG. Roughly half the bytes for the
 * same picture; `<Photo>` offers it first and lets the browser fall back to
 * the JPEG on its own.
 */
export function webpSrcSetFor(src: string, widths: number[]): string {
  if (!/\.jpg$/i.test(src)) return "";
  return widths.map((w) => `${src.replace(/\.jpg$/i, `-${w}.webp`)} ${w}w`).join(", ");
}

/** Full-bleed images: always one viewport wide. */
export const SIZES_FULL_BLEED = "100vw";

/** Two-column card grid (single column below the sm breakpoint). */
export const SIZES_TWO_COL = "(min-width: 640px) 50vw, 100vw";

/** Three-column card grid stepping down to two, then one. */
export const SIZES_THREE_COL = "(min-width: 1024px) 33vw, (min-width: 640px) 50vw, 100vw";
